import { useState, useEffect } from 'react';
import { Investor } from '@/types/investor';
import { InvestorService, type ArchivedInvestorSummary } from '@/services/investorService';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { toast } from 'sonner';

const log = createComponentLogger('useInvestors', 'hook');

export interface BulkArchiveInvestorsResult {
  archived: Investor[];
  failed: Array<{
    investorId: string;
    investorName: string;
    error: string;
  }>;
}

export const useInvestors = () => {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(true);
  const [archivedInvestors, setArchivedInvestors] = useState<ArchivedInvestorSummary[]>([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Get organization ID and user ID on mount
  useEffect(() => {
    const getOrganizationId = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          log.warn('No authenticated user found');
          setLoading(false);
          return;
        }

        // Store the user ID
        setUserId(user.id);

        const { data: profile, error } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', user.id)
          .single();

        if (error || !profile) {
          log.error('Failed to get user profile', { error });
          setLoading(false);
          return;
        }

        setOrganizationId(profile.organization_id);
      } catch (error) {
        log.error('Error getting organization ID', { error });
        setLoading(false);
      }
    };

    getOrganizationId();
  }, []);

  // Load investors when organization ID is available
  useEffect(() => {
    if (!organizationId) {
return;
}

    const loadInvestors = async () => {
      try {
        log.info('Loading investors from database', { organizationId, userId });
        // Pass userId to include user's drafts
        const data = await InvestorService.getInvestors(organizationId, userId || undefined);
        setInvestors(data);
        log.info('Investors loaded successfully', { count: data.length, includesDrafts: !!userId });
      } catch (error) {
        log.error('Failed to load investors', { error });
        toast.error('Failed to load investors');
      } finally {
        setLoading(false);
      }
    };

    loadInvestors();
  }, [organizationId, userId]);

  const addInvestor = async (investor: Investor): Promise<void> => {
    if (!organizationId) {
      toast.error('Organization ID not found');
      throw new Error('Organization ID not found');
    }

    try {
      log.info('Adding investor', { investorName: investor.investor });
      const created = await InvestorService.createInvestor(investor, organizationId);
      setInvestors(prev => [created, ...prev]);
      log.info('Investor added successfully', { investorId: created.id });
    } catch (error) {
      log.error('Failed to add investor', { error });
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to create investor');
      throw error;
    }
  };

  const updateInvestor = async (updatedInvestor: Investor): Promise<void> => {
    if (!organizationId) {
      toast.error('Organization ID not found');
      throw new Error('Organization ID not found');
    }

    try {
      log.info('Updating investor', { investorId: updatedInvestor.id });
      const updated = await InvestorService.updateInvestor(updatedInvestor, organizationId);

      // Check if investor exists in local state
      setInvestors(prev => {
        const exists = prev.some(inv => inv.id === updated.id);
        if (exists) {
          // Update existing investor
          return prev.map(inv => inv.id === updated.id ? updated : inv);
        } else {
          // Draft being published - add it to the list
          log.info('Adding published draft to investor list', { investorId: updated.id });
          return [updated, ...prev];
        }
      });

      log.info('Investor updated successfully', { investorId: updated.id });
    } catch (error) {
      log.error('Failed to update investor', { error });
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to update investor');
      throw error;
    }
  };

  const archiveInvestor = async (investorId: string): Promise<Investor> => {
    if (!organizationId) {
      throw new Error('Organization ID not found');
    }

    const investor = investors.find((item) => item.id === investorId);
    if (!investor) {
      throw new Error('Investor not found in the active list');
    }

    log.info('Archiving investor', { metadata: { investorId } });
    await InvestorService.archiveInvestor(investorId);

    // Update the UI only after the database transaction confirms the archive.
    setInvestors((previous) => previous.filter((item) => item.id !== investorId));
    log.info('Investor archived successfully', { metadata: { investorId } });
    return investor;
  };

  const bulkArchiveInvestors = async (investorIds: string[]): Promise<BulkArchiveInvestorsResult> => {
    if (!organizationId) {
      throw new Error('Organization ID not found');
    }

    const investorsById = new Map(investors.map((investor) => [investor.id, investor]));
    log.info('Bulk archiving investors', {
      metadata: { count: investorIds.length }
    });

    const results = await Promise.allSettled(
      investorIds.map((investorId) => InvestorService.archiveInvestor(investorId))
    );
    const archived: Investor[] = [];
    const archivedIds = new Set<string>();
    const failed: BulkArchiveInvestorsResult['failed'] = [];

    results.forEach((result, index) => {
      const investorId = investorIds[index];
      const investor = investorsById.get(investorId);

      if (result.status === 'fulfilled') {
        archivedIds.add(investorId);
        if (investor) {
          archived.push(investor);
        }
        return;
      }

      failed.push({
        investorId,
        investorName: investor?.investor || 'Unknown investor',
        error: result.reason instanceof Error ? result.reason.message : 'Archive failed'
      });
    });

    setInvestors((previous) => previous.filter((investor) => !archivedIds.has(investor.id)));
    log.info('Bulk archive completed', {
      metadata: {
        archived: archivedIds.size,
        failed: failed.length
      }
    });

    return { archived, failed };
  };

  const getInvestorById = (investorId: string): Investor | undefined => {
    return investors.find(inv => inv.id === investorId);
  };

  const loadArchivedInvestors = async (): Promise<void> => {
    if (!organizationId) {
      throw new Error('Organization ID not found');
    }

    setArchivedLoading(true);
    try {
      const archived = await InvestorService.getArchivedInvestors(organizationId);
      setArchivedInvestors(archived);
    } finally {
      setArchivedLoading(false);
    }
  };

  const restoreInvestor = async (investorId: string): Promise<Investor> => {
    if (!organizationId) {
      throw new Error('Organization ID not found');
    }

    await InvestorService.restoreInvestor(investorId);
    const restored = await InvestorService.getInvestorById(investorId, organizationId);
    if (!restored) {
      throw new Error('The investor was restored but could not be reloaded');
    }

    setArchivedInvestors((previous) => previous.filter((investor) => investor.id !== investorId));
    setInvestors((previous) => [restored, ...previous.filter((investor) => investor.id !== investorId)]);
    return restored;
  };

  /**
   * Refresh a specific investor's data from the database
   * Useful after document uploads to get the latest documents
   */
  const refreshInvestor = async (investorId: string): Promise<void> => {
    if (!organizationId) {
      log.warn('Cannot refresh investor: no organization ID');
      return;
    }

    try {
      log.info('Refreshing investor data', { investorId });
      const refreshed = await InvestorService.getInvestorById(investorId, organizationId);

      if (refreshed) {
        setInvestors(prev => prev.map(inv => inv.id === investorId ? refreshed : inv));
        log.info('Investor data refreshed', { investorId });
      }
    } catch (error) {
      log.error('Failed to refresh investor', { error, investorId });
    }
  };

  return {
    investors,
    archivedInvestors,
    loading,
    archivedLoading,
    organizationId,
    addInvestor,
    updateInvestor,
    archiveInvestor,
    bulkArchiveInvestors,
    loadArchivedInvestors,
    restoreInvestor,
    getInvestorById,
    refreshInvestor
  };
};
